"use client";

import { useState, useEffect } from "react";
import { X, Search, Building2, Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { tenantApi } from "@/lib/api-client";

interface TenantSwitchDialogProps { open: boolean; onClose: () => void; }
interface TenantItem { id: string; name: string; code: string; modelType: string; status: string; current: boolean; starred: boolean; }

export function TenantSwitchDialog({ open, onClose }: TenantSwitchDialogProps) {
  const { tenant: currentTenant } = useAuth();
  const [tenants, setTenants] = useState<TenantItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [starredIds, setStarredIds] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem("ts_starred_tenants") || "[]")); } catch { return new Set<string>(); } });
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    tenantApi.page({ pageNum: 1, pageSize: 100 }).then(res => {
      if (res.code === 200 && res.data?.records) {
        const list = (res.data.records as any[]).map((t: any) => ({
          id: t.id, name: t.name || "", code: t.code || "",
          modelType: t.modelType || t.model_type || "STANDARD", status: t.status || "active",
          current: t.id === currentTenant?.id, starred: starredIds.has(t.id),
        }));
        setTenants(list);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open, currentTenant?.id]);

  const filteredTenants = tenants.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.code.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleSwitch = async () => {
    if (!selectedTenant || selectedTenant === currentTenant?.id) return;
    setSwitching(true);
    const target = tenants.find(t => t.id === selectedTenant);
    if (target) {
      localStorage.setItem("ts_tenant", JSON.stringify({ id: target.id, name: target.name, code: target.code, modelType: target.modelType, status: target.status }));
      localStorage.setItem("ts_tenantId", target.id);
    }
    setSwitching(false);
    onClose();
    window.location.reload();
  };

  const toggleStar = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const ns = new Set(starredIds);
    ns.has(id) ? ns.delete(id) : ns.add(id);
    setStarredIds(ns);
    localStorage.setItem("ts_starred_tenants", JSON.stringify([...ns]));
  };

  const getTypeColor = (t: string) => ({ GROUP: "bg-purple-100 text-purple-700 border-purple-200", CHANNEL: "bg-blue-100 text-blue-700 border-blue-200", STANDARD: "bg-cyan-100 text-cyan-700 border-cyan-200", COMMUNITY: "bg-amber-100 text-amber-700 border-amber-200", PERSONAL: "bg-green-100 text-green-700 border-green-200" }[t] || "bg-gray-100 text-gray-700 border-gray-200");
  const getTypeLabel = (t: string) => ({ GROUP: "集团型", CHANNEL: "渠道型", STANDARD: "标准型", COMMUNITY: "社团型", PERSONAL: "个人型" }[t] || t);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-[700px] max-h-[80vh] bg-white rounded-xl shadow-2xl border border-border/50 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <div className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#2563EB]" /><h2 className="text-base font-semibold">切换租户</h2><Badge variant="secondary" className="text-xs">{tenants.length} 个租户</Badge></div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-border/30">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input type="text" placeholder="搜索租户名称..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-9 pl-9 pr-4 rounded-lg bg-gray-50 border border-transparent focus:border-[#2563EB]/30 focus:bg-white focus:outline-none text-sm" autoFocus /></div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {loading ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#2563EB]" /></div> :
           filteredTenants.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground">未找到匹配的租户</div> :
           <div className="space-y-1 px-3">
             {filteredTenants.map(t => (
               <button key={t.id} onClick={() => !t.current && setSelectedTenant(t.id)}
                 className={cn("w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left", t.current ? "bg-[#2563EB]/5 border border-[#2563EB]/20" : selectedTenant === t.id ? "bg-[#2563EB]/10 border border-[#2563EB]/30" : "hover:bg-gray-50 border border-transparent")}>
                 <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", t.current ? "bg-[#2563EB]/20" : "bg-gray-100")}><Building2 className={cn("w-5 h-5", t.current ? "text-[#2563EB]" : "text-muted-foreground")} /></div>
                 <div className="flex-1 min-w-0">
                   <div className="flex items-center gap-2"><span className="text-sm font-medium truncate">{t.name}</span><Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", getTypeColor(t.modelType))}>{getTypeLabel(t.modelType)}</Badge>{t.current && <Badge className="text-[10px] px-1.5 py-0 bg-[#2563EB] text-white">当前</Badge>}</div>
                   <div className="text-xs text-muted-foreground mt-0.5">{t.code}</div>
                 </div>
                 <button onClick={(e) => toggleStar(t.id, e)} className="p-1"><Star className={cn("w-4 h-4", t.starred ? "fill-yellow-400 text-yellow-400" : "text-gray-300 hover:text-gray-400")} /></button>
               </button>
             ))}
           </div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border/50 bg-gray-50/50">
          <Button variant="outline" size="sm" onClick={onClose}>取消</Button>
          <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white" disabled={!selectedTenant || selectedTenant === currentTenant?.id || switching} onClick={handleSwitch}>
            {switching && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}切换
          </Button>
        </div>
      </div>
    </div>
  );
}
